"use strict"

/* ===========================================================================================================================
 * This is a work in progress...
 * Attempting to develop a single library for all 508 requirements.
 * It currently handles arrow and tab key functionality for tab modals.
 * 
 * Component Implementation-------------------------------------------------
 * To implement, instantiate the library and call initalize508 once the DOM is active with or without a container. 
 * 
 * Each page needs to instantiate separately because each page has a unique list of elements.
 * 
 * HTML Implementation------------------------------------------------------
 * Every tab modal tab element needs class cpd-508 and set attribute cpd-508Index. cpd-508Index can start at any number.
 * ex: [attr.cpd-508Index]="0"
 * 
 * Tabs must be button elements with roll="tab".
 * For tabs, it is recommended to use automatic activation by opening the tab on (focus) as opposed to when enter is hit 
 * so that the next element is not hidden.
 * For tabs, you need to focus on the open tab text content on (click) so that the enter key will jump to the text content.
 * 
 * Additionally, all 508 tab elements must be wrapped in a section or div having role="application" in order to
 * override JAWS arrow key functions. Otherwise JAWS will steal the arrow key press and this class will do nothing.
 * 
 * fpierce@ASRCFederal.com
 *============================================================================================================================ 
 * */

export class Accessibility508 {
	_containerId = '';
	_event = null;
	_elements = null;
	_elementArray = [];
	_currentElement = null;
	_currentElementArrayIndex = 0;
	_destinationElementArrayIndex = 0;
	_destinationElement = null;
	_wrapAroundCount = 0; // Infinite loop prevention

	//Methods
	//-------------------------------------------------------------------------------------------------------------------------
	initalize508(containerId) {
		try {
			if (containerId) {
				this._containerId = containerId;
				this.setElementsUsingContainerId();
			}
			else {
				this.setElementsWithoutContainerId();
			}
			this.validateElements();
			this.createSortedArrayOfElements();
			this.bindArrayElementsToKeyDownEventFunction();
			this.setButtonInitialTabIndexes();
		}
		catch (error) {
			console.log("508 error: ", error);
        }
	}

    setElementsUsingContainerId() {
        this._elements = document.getElementById(this._containerId).getElementsByClassName("cpd-508");
	}

	setElementsWithoutContainerId() {
		this._elements = document.getElementsByClassName("cpd-508");
	}

	validateElements() {
		if (!this._elements || this._elements.length <= 1) {
			throw "No 508 elements found on initalization. Check HTML element classes.";
		}
	}

    createSortedArrayOfElements() {
        if (this._elements.length > 1) {
            this._elementArray = Array.prototype.slice.call(this._elements);
            this._elementArray = this._elementArray.sort(this.compareElementsBy508Index);
        }
	}

	compareElementsBy508Index(firstElement, secondElement) {
		let firstElementCpd508Index = firstElement.getAttribute("cpd-508Index");
		let secondElementCpd508Index = secondElement.getAttribute("cpd-508Index");
		if (firstElementCpd508Index < secondElementCpd508Index) {
			return -1;
		}
		if (firstElementCpd508Index > secondElementCpd508Index) {
			return 1;
		}
		return 0;
	}

	bindArrayElementsToKeyDownEventFunction() {
		this._elementArray.forEach(element => {
			element.onkeydown = (event) => { this.handle508KeyPress(event); };
		});
	}

	setButtonInitialTabIndexes() {
		let firstButton = true;
		this._elementArray.forEach((element) => {
			if (element.nodeName !== "BUTTON") {
				return;
			}
			if (firstButton) {
				element.setAttribute("tabindex", "0");
				firstButton = false;
			}
			else {
				element.setAttribute("tabindex", "-1");
			}
		});
    }

	handle508KeyPress(event) {
		this._event = event;
		try {
			this.validateImplementation();
			this.findStartingLocation();
			if (this.startingLocationIsInvalid()) {
				return;
			}
			this.executeKeyRouting();
		}
		catch (error) {
			console.log("508 error: ", error);
        }
	}

	validateImplementation() {
		if (!this._elementArray || this._elementArray.length === 0) {
			throw "No element array";
		}
		if (this._elementArray.length < 2) {
			throw "Element array not long enough";
		}
	}

	findStartingLocation() {
		this.setCurrentElement();
		this.setCurrentElementArrayIndex();
	}

    setCurrentElement() {
        this._currentElement = document.activeElement;
	}

	setCurrentElementArrayIndex() {
		this._currentElementArrayIndex = this._elementArray.findIndex((arrayElement) => {
			return arrayElement.getAttribute("cpd-508Index") === this._currentElement.getAttribute("cpd-508Index");
		});
	}

	startingLocationIsInvalid() {
		return this._currentElementArrayIndex === -1;
	}

	executeKeyRouting() {
		if (this._event.key === "ArrowLeft") {
			this.setDestinationBackward();
			this.goToDestination();
		}
		if (this._event.key === "ArrowRight") {
			this.setDestinationForward();
			this.goToDestination();
		}
	}

	setDestinationBackward() {
		this._destinationElementArrayIndex = this._currentElementArrayIndex - 1;
	}

	setDestinationForward() {
		this._destinationElementArrayIndex = this._currentElementArrayIndex + 1;
	}

	goToDestination() {
		if (this.stuckInLoop()) {
			this.resetWrapAroundCount();
			return;
        }
		this.executeWrapAroundLogic();
		this.findDestinationElement();
        this.validateDestinationElement();
		if (this.destinationElementIsNotAButton()) {
			this.skipToNextElement();
			this.goToDestination();
			return;
		}
		this.focusOnDestinationElement();
		this.resetWrapAroundCount();
		this.setButtonTabIndexes();
	}

	stuckInLoop() {
		return this._wrapAroundCount > 1;
	}

	resetWrapAroundCount() {
		this._wrapAroundCount = 0;
	}

    executeWrapAroundLogic() {
        if (this.destinationBeforeBeginningOfArray()) {
			this.setDestinationToEndOfArray();
			this.incrementWrapAroundCount();
        }
        if (this.destinationPastEndOfArray()) {
			this.setDestinationToBeginningOfArray();
			this.incrementWrapAroundCount();
        }
    }

    destinationBeforeBeginningOfArray() {
        return this._destinationElementArrayIndex < 0;
	}

	setDestinationToEndOfArray() {
		this._destinationElementArrayIndex = this._elementArray.length - 1;
	}

	incrementWrapAroundCount() {
		this._wrapAroundCount++;
	}

	destinationPastEndOfArray() {
		return this._destinationElementArrayIndex > this._elementArray.length - 1;
	}

	setDestinationToBeginningOfArray() {
		this._destinationElementArrayIndex = 0;
	}

    findDestinationElement() {
        this._destinationElement = this._elementArray[this._destinationElementArrayIndex];
    }

	validateDestinationElement() {
		if (!this._destinationElement) {
			throw "Destination element does not exist.";
		}
		if (!this._destinationElement.focus) {
			throw "Destination element has no focus function.";
		}
	}

	destinationElementIsNotAButton() {
		return this._destinationElement.nodeName !== "BUTTON";
	}

	skipToNextElement() {
		if (this.movingForward()) {
			this.incrementDestination();
        }
        else {
			this.decrementDestination();
		}
	}

	movingForward() {
		return (this._destinationElementArrayIndex > this._currentElementArrayIndex)
			&& !(this._currentElementArrayIndex === 0 && this._destinationElementArrayIndex === this._elementArray.length - 1);
	}

    incrementDestination() {
        this._destinationElementArrayIndex++;
    }

    decrementDestination() {
        this._destinationElementArrayIndex--;
	}

	focusOnDestinationElement() {
		this._destinationElement.focus();
	}

	setButtonTabIndexes() {
		this._elementArray.forEach((element) => {
			if (element.nodeName !== "BUTTON") {
				return;
			}
			if (element.getAttribute("cpd-508Index") === this._destinationElement.getAttribute("cpd-508Index")) {
				element.setAttribute("tabindex", "0");
			}
			else {
				element.setAttribute("tabindex", "-1");
            }
		});
	}
}
